// Dashboard charts functionality for ApexCharts
export function initializeDashboardCharts() {
  // Get chart data from data attributes
  const chartContainer = document.querySelector('[data-dashboard-charts]');
  if (!chartContainer) {
    // Only warn if we're on a page that should have charts (dashboard page)
    const isDashboardPage = window.location.pathname.includes('/dashboard') || 
                           document.querySelector('[data-dashboard-container]');
    if (isDashboardPage) {
      console.warn('Dashboard charts container not found');
    }
    return;
  }

  // Parse chart data from data attributes with error handling
  let chartData = {};
  
  try {
    chartData.weeklyPie = JSON.parse(chartContainer.dataset.weeklyPie || '{"series": [], "labels": []}');
  } catch (e) {
    console.warn('Error parsing weekly pie chart data:', e);
    chartData.weeklyPie = { series: [], labels: [] };
  }
  
  try {
    chartData.weeklyColumn = JSON.parse(chartContainer.dataset.weeklyColumn || '{"series": [], "categories": []}');
  } catch (e) {
    console.warn('Error parsing weekly column chart data:', e);
    chartData.weeklyColumn = { series: [], categories: [] };
  }
  
  try {
    chartData.dailyHours = JSON.parse(chartContainer.dataset.dailyHours || '{"series": [], "categories": []}');
  } catch (e) {
    console.warn('Error parsing daily hours chart data:', e);
    chartData.dailyHours = { series: [], categories: [] };
  }
  
  try {
    chartData.topCategories = JSON.parse(chartContainer.dataset.topCategories || '{"categories": [], "series": [{"data": []}]}');
  } catch (e) {
    console.warn('Error parsing top categories chart data:', e);
    chartData.topCategories = { categories: [], series: [{ data: [] }] };
  }

  // Empty state helper
  function showEmptyChart(chartId, message = 'No data available') {
    const container = document.getElementById(chartId);
    if (container) {
      container.innerHTML = `
        <div class="flex items-center justify-center" style="height: 420px;">
          <div class="text-center">
            <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            <p class="text-sm text-gray-500 dark:text-gray-400">${message}</p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Start tracking time to see your analytics</p>
          </div>
        </div>
      `;
    }
  }

  // Destroy existing charts if they exist
  if (window.pieChart) {
    window.pieChart.destroy();
    window.pieChart = null;
  }
  if (window.columnChart) {
    window.columnChart.destroy();
    window.columnChart = null;
  }
  if (window.dailyHoursChart) {
    window.dailyHoursChart.destroy();
    window.dailyHoursChart = null;
  }
  if (window.topCategoriesChart) {
    window.topCategoriesChart.destroy();
    window.topCategoriesChart = null;
  }

  // Chart color palette
  const chartColors = [
    "#1C64F2", "#16BDCA", "#9061F9", "#FDBA8C", "#E74C3C", "#6875F5", 
    "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#84CC16",
    "#F97316", "#EC4899", "#6366F1", "#14B8A6", "#F59E0B", "#DC2626",
    "#7C3AED", "#0891B2", "#65A30D", "#EA580C", "#DB2777", "#4F46E5",
    "#059669", "#D97706", "#B91C1C", "#6D28D9", "#0E7490", "#4D7C0F",
    "#C2410C", "#BE185D", "#3730A3", "#047857", "#92400E", "#991B1B"
  ];

  // Pie Chart - Time per Category This Week
  const pieChartData = chartData.weeklyPie.series;
  if (pieChartData.length === 0) {
    showEmptyChart('pie-chart', 'No time logged for this period');
  } else {
    const pieChartOptions = {
      series: pieChartData,
      colors: chartColors,
      chart: {
        height: 453,
        width: "100%",
        type: "pie",
      },
      stroke: {
        colors: ["white"],
        lineCap: "",
      },
      plotOptions: {
        pie: {
          labels: {
            show: true,
          },
          size: "100%",
          dataLabels: {
            offset: -25
          }
        },
      },
      labels: chartData.weeklyPie.labels,
      dataLabels: {
        enabled: true,
        style: {
          fontFamily: "Inter, sans-serif",
        },
      },
      legend: {
        position: "bottom",
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
        markers: {
          width: 12,
          height: 12,
          radius: 2
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4
        }
      },
    };

    if (document.getElementById("pie-chart") && typeof ApexCharts !== 'undefined') {
      window.pieChart = new ApexCharts(document.getElementById("pie-chart"), pieChartOptions);
      window.pieChart.render();
    }
  }

  // Column Chart - Hours per Category This Week  
  const columnChartData = chartData.weeklyColumn.series;
  const hasValidColumnData = columnChartData.length > 0 && 
                            columnChartData[0] && 
                            columnChartData[0].data && 
                            columnChartData[0].data.length > 0 &&
                            columnChartData[0].data.some(value => value > 0);
  
  if (!hasValidColumnData) {
    showEmptyChart('column-chart', 'No time logged for this period');
  } else {
    const columnChartOptions = {
      colors: chartColors,
      series: columnChartData,
      chart: {
        type: "bar",
        height: "420px",
        fontFamily: "Inter, sans-serif",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadiusApplication: "end",
          borderRadius: 8,
          distributed: true,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: "Inter, sans-serif",
        },
      },
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        show: true,
        width: 0,
        colors: ["transparent"],
      },
      grid: {
        show: false,
        strokeDashArray: 4,
        padding: {
          left: 2,
          right: 2,
          top: -14
        },
      },
      dataLabels: {
        enabled: true,
      },
      legend: {
        show: true,
        position: "bottom",
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
        markers: {
          width: 12,
          height: 12,
          radius: 2
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4
        }
      },
      xaxis: {
        floating: false,
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        categories: chartData.weeklyColumn.categories,
      },
      yaxis: {
        show: false,
      },
      fill: {
        opacity: 1,
      },
    };

    if(document.getElementById("column-chart") && typeof ApexCharts !== 'undefined') {
      window.columnChart = new ApexCharts(document.getElementById("column-chart"), columnChartOptions);
      window.columnChart.render();
    }
  }

  // Daily Hours Chart
  const dailyHoursData = chartData.dailyHours.series;
  const hasValidDailyData = dailyHoursData.length > 0 && 
                           dailyHoursData[0] && 
                           dailyHoursData[0].data && 
                           dailyHoursData[0].data.length > 0 &&
                           dailyHoursData[0].data.some(value => value > 0);
  
  if (!hasValidDailyData) {
    showEmptyChart('daily-hours-chart', 'No time logged for this period');
  } else {
    const dailyHoursOptions = {
      colors: chartColors,
      series: dailyHoursData,
      chart: {
        type: "bar",
        height: "420px",
        fontFamily: "Inter, sans-serif",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadiusApplication: "end",
          borderRadius: 8,
          distributed: true,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: "Inter, sans-serif",
        },
      },
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        show: true,
        width: 0,
        colors: ["transparent"],
      },
      grid: {
        show: false,
        strokeDashArray: 4,
        padding: {
          left: 2,
          right: 2,
          top: -14
        },
      },
      dataLabels: {
        enabled: true,
      },
      legend: {
        show: false,
      },
      xaxis: {
        floating: false,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: 'text-xs font-normal fill-gray-500 dark:fill-gray-400'
          }
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        categories: chartData.dailyHours.categories,
      },
      yaxis: {
        show: false,
      },
      fill: {
        opacity: 1,
      },
    };

    if(document.getElementById("daily-hours-chart") && typeof ApexCharts !== 'undefined') {
      window.dailyHoursChart = new ApexCharts(document.getElementById("daily-hours-chart"), dailyHoursOptions);
      window.dailyHoursChart.render();
    }
  }

  // Top Categories Chart
  const topCategoriesData = chartData.topCategories.categories.map((cat, index) => ({
    x: cat,
    y: chartData.topCategories.series[0]?.data[index] || 0
  }));
  
  if (topCategoriesData.length === 0 || topCategoriesData.every(item => item.y === 0)) {
    showEmptyChart('top-categories-chart', 'No time logged yet');
  } else {
    const topCategoriesOptions = {
      colors: chartColors,
      series: [{
        name: "Hours",
        data: topCategoriesData
      }],
      chart: {
        type: "bar",
        height: "420px",
        fontFamily: "Inter, sans-serif",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          columnWidth: "70%",
          borderRadiusApplication: "end",
          borderRadius: 8,
          distributed: true,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: "Inter, sans-serif",
        },
      },
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 1,
          },
        },
      },
      stroke: {
        show: true,
        width: 0,
        colors: ["transparent"],
      },
      grid: {
        show: false,
        strokeDashArray: 4,
        padding: {
          left: 60,
          right: 12,
          top: -14,
          bottom: 10
        },
      },
      dataLabels: {
        enabled: true,
      },
      legend: {
        show: false,
      },
      xaxis: {
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          show: true,
          maxWidth: 200,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: 'text-xs font-normal fill-gray-500 dark:fill-gray-400'
          },
          offsetX: -10,
        },
      },
      fill: {
        opacity: 1,
        colors: chartColors
      },
    };

    if(document.getElementById("top-categories-chart") && typeof ApexCharts !== 'undefined') {
      window.topCategoriesChart = new ApexCharts(document.getElementById("top-categories-chart"), topCategoriesOptions);
      window.topCategoriesChart.render();
    }
  }
}

// Wait for ApexCharts to be available before initializing charts
export function waitForApexChartsAndInitialize() {
  if (typeof ApexCharts !== 'undefined') {
    initializeDashboardCharts();
  } else {
    // Check again in 50ms if ApexCharts isn't loaded yet
    setTimeout(waitForApexChartsAndInitialize, 50);
  }
}

// Setup function for initialization
export function setupDashboardCharts() {
  // Initialize charts when this partial loads, but wait for ApexCharts
  waitForApexChartsAndInitialize();
} 